import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { RootState } from '../store';

interface ContactFormData {
    name: string;
    email: string;
    phone: string;
    subject: string;
    message: string;
}

interface ContactState {
    loading: 'idle' | 'pending' | 'succeeded' | 'failed';
    error: string | null;
    success: boolean;
}

const initialState: ContactState = {
    loading: 'idle',
    error: null,
    success: false,
};

// Async thunk for submitting contact form
export const submitContactForm = createAsyncThunk<{ success: boolean }, ContactFormData, { rejectValue: string }>(
    'contact/submitForm',
    async (formData, { rejectWithValue }) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const response = await axios.post(`${backendUrl}/contact`, formData);
            
            // Check if the API response indicates success
            if (response.data.success) {
                return { success: true };
            } else {
                return rejectWithValue(response.data.message || 'Failed to submit contact form.');
            }
        } catch (err: any) {
            if (axios.isAxiosError(err) && err.response) {
                return rejectWithValue(err.response.data.message || 'Failed to submit contact form.');
            }
            return rejectWithValue('An unknown error occurred.');
        }
    }
);

const contactSlice = createSlice({
    name: 'contact',
    initialState,
    reducers: {
        resetContactState: (state) => {
            state.loading = 'idle';
            state.error = null;
            state.success = false;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(submitContactForm.pending, (state) => {
                state.loading = 'pending';
                state.error = null;
                state.success = false;
            })
            .addCase(submitContactForm.fulfilled, (state) => {
                state.loading = 'succeeded';
                state.success = true;
            })
            .addCase(submitContactForm.rejected, (state, action) => {
                state.loading = 'failed';
                state.error = action.payload ?? 'Failed to submit';
                state.success = false;
            });
    },
});

export const { resetContactState } = contactSlice.actions;
export const selectContact = (state: RootState) => state.contact;

export default contactSlice.reducer; 